import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, ExternalLink, Trash2, Bookmark, ChevronDown, ChevronUp, ShieldCheck, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { type LibraryEntry, getLibrary, removeFromLibrary, clearLibrary } from "@/lib/infiniteSelves/library";
interface SavedPossibilitiesProps {
  currentCode: string | null;
  onSelectCode: (code: string) => void;
  onClearCurrent?: () => void;
}
export default function SavedPossibilities({
  currentCode,
  onSelectCode
}: SavedPossibilitiesProps): JSX.Element {
  const [library, setLibrary] = useState<LibraryEntry[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const refreshData = useCallback(() => {
    setLibrary(getLibrary());
  }, []);
  useEffect(() => {
    refreshData();
    // Listen for custom or storage events across tabs if any
    const handleStorage = () => refreshData();
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [refreshData, currentCode]);
  const handleRemove = (e: React.MouseEvent, code: string) => {
    e.stopPropagation();
    removeFromLibrary(code);
    refreshData();
  };
  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm("Clear all your saved multiverse timeline configurations from this browser?")) {
      clearLibrary();
      refreshData();
    }
  };
  const handleOpenEntry = (code: string) => {
    onSelectCode(code);
  };
  const totalCount = library.length;
  const formatDate = (ts: number) => {
    try {
      return new Date(ts).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "";
    }
  };
  return <div className="w-full max-w-4xl mx-auto my-6">
      <div className="ql-glass-strong rounded-2xl border border-primary/30 p-4 sm:p-5 shadow-xl transition-all">
        {/* Accordion / Header Bar */}
        <div onClick={() => setIsOpen(prev => !prev)} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer select-none" role="button" tabIndex={0} onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsOpen(prev => !prev);
        }
      }} aria-expanded={isOpen} aria-label="Toggle saved multiverse possibilities panel">
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-xl bg-primary/10 border border-primary/30 text-primary shrink-0 shadow-[0_0_8px_var(--glow-primary)]">
              <Bookmark className="size-4" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-serif text-sm sm:text-base font-bold text-foreground truncate">
                  Your Generated Possibilities
                </h3>
                <Badge variant="outline" className="font-mono text-[10px] border-primary/40 text-primary bg-primary/10">
                  {totalCount} {totalCount === 1 ? "timeline" : "timelines"}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground truncate hidden sm:block">
                Saved locally in this browser · Revisit past multiverse coordinates anytime.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
            {library.length > 0 && isOpen && <button type="button" onClick={handleClearAll} className="text-[11px] font-mono text-muted-foreground hover:text-destructive transition-colors px-2 py-1 cursor-pointer mr-1">
                Clear all
              </button>}
            <button type="button" className="ql-btn-ghost rounded-full size-8 p-0 flex items-center justify-center text-primary" aria-hidden="true">
              {isOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            </button>
          </div>
        </div>

        {/* Collapsed/Expanded Content */}
        {isOpen && <div className="mt-5 space-y-4 pt-4 border-t border-border/60">
            {/* Privacy notice banner */}
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-card/60 border border-border/80 text-[11px] text-muted-foreground font-sans">
              <ShieldCheck className="size-3.5 text-primary shrink-0 mt-0.5" />
              <span>
                Saved only in this browser; only the settings code is kept, so the same code rebuilds the same four lives.
              </span>
            </div>

            {/* Library Entries List */}
            {library.length > 0 ? <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {library.map(entry => {
              const isSelected = currentCode === entry.code;
              const nicknameLabel = entry.nickname || "Traveler";
              return <div key={entry.code} onClick={() => handleOpenEntry(entry.code)} className={`p-3.5 rounded-xl border transition-all cursor-pointer flex flex-col justify-between gap-3 ${isSelected ? "bg-card border-primary ring-1 ring-primary/50 shadow-[0_0_16px_var(--glow-primary)]" : "bg-card/75 border-border hover:border-primary/40 hover:bg-card"}`}>
                        <div className="space-y-1.5 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-serif text-sm font-bold text-foreground truncate">
                              {nicknameLabel}
                            </span>
                            <Badge variant="outline" className="text-[9px] font-mono text-muted-foreground border-border/80 shrink-0">
                              {entry.universeMode === "shared" ? "Shared cosmos" : "Independent"}
                            </Badge>
                          </div>

                          <div className="flex items-center gap-2 text-[10px] font-mono text-primary">
                            <span>{entry.variationId}</span>
                            <span className="text-muted-foreground">·</span>
                            <span className="text-muted-foreground">{formatDate(entry.savedAt)}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-2 border-t border-border/40 pt-2">
                          <button type="button" onClick={e => {
                    e.stopPropagation();
                    handleOpenEntry(entry.code);
                  }} className="ql-btn min-h-[32px] px-3 text-[11px] font-serif">
                            Open
                          </button>
                          <button type="button" onClick={e => handleRemove(e, entry.code)} title="Remove timeline from browser" className="size-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                      </div>;
            })}
                </div>
              </div> : totalCount === 0 ? <div className="p-6 text-center text-xs text-muted-foreground space-y-1">
                <p>No saved timelines yet.</p>
                <p className="text-[11px]">
                  Whenever you reveal a set of lives or explore new variations, they will automatically be cataloged here for quick return.
                </p>
              </div> : null}
          </div>}
      </div>
    </div>;
}